/** @format */
import { Aside } from '@mpb-com/feather/components/atoms/aside';
import {
    Icon,
    IconSize,
    IconVariant,
} from '@mpb-com/feather/components/atoms/icon';
import { useMeasure } from '@mpb-com/feather/hooks/use-measure';
import { animated, useSpring } from '@react-spring/web';
import { useTranslation } from 'next-i18next';
import React, {
    FunctionComponent,
    ReactNode,
    useEffect,
    useRef,
    useState,
} from 'react';
import styled from 'styled-components';

import ChevronDown from '@toucan/client/global/icons/chevron-down.svg';

import { AsideMenuStyleProps, styles } from './aside-menu.styles';

export type AsideMenuProps = {
    children: ReactNode;
    'data-testid'?: string;
    className?: string;
    title: ReactNode;
    onClick?: (isOpen: boolean) => void;
    titleExtraContent?: ReactNode;
};

const StyledAsideMenu = styled('div')<AsideMenuStyleProps>`
    ${styles}
`;

export const AsideMenu: FunctionComponent<AsideMenuProps> = ({
    title,
    titleExtraContent,
    children,
    onClick,
    ...otherProps
}: AsideMenuProps) => {
    const { t } = useTranslation();
    const [isOpen, setIsOpen] = useState<boolean>(false);

    const [contentHeight, setContentHeight] = useState(0);
    const ref = useRef<HTMLDivElement>(null);
    const { height } = useMeasure(ref);

    const contentSpringStyle = useSpring({
        height: isOpen ? contentHeight : 0,
    });

    function toggleOpen() {
        setIsOpen(!isOpen);
        onClick?.(!isOpen);
    }

    useEffect(() => {
        setContentHeight(height);
        window.addEventListener('resize', () => setContentHeight(height));

        return window.removeEventListener('resize', () =>
            setContentHeight(height)
        );
    }, [height]);

    return (
        <StyledAsideMenu
            $isOpen={isOpen}
            data-testid="aside-menu"
            {...otherProps}
        >
            <Aside border>
                <div className="title-container">
                    <div
                        className="title"
                        data-testid="aside-menu__title"
                        onClick={toggleOpen}
                    >
                        {title}
                        <Icon
                            variant={IconVariant.CORE}
                            className="toggle-button"
                            aria-label={t('Expand')}
                            size={IconSize.S}
                            data-testid="aside-menu__toggle-button"
                        >
                            <ChevronDown />
                        </Icon>

                        <div
                            className="title-extra-content"
                            data-testid="aside-menu__title__extra-content"
                        >
                            {titleExtraContent}
                        </div>
                    </div>
                </div>

                <animated.div
                    style={contentSpringStyle}
                    className="content-container"
                    data-testid="aside-menu__content"
                    aria-hidden={!isOpen}
                >
                    <div className="content" ref={ref}>
                        {children}
                    </div>
                </animated.div>
            </Aside>
        </StyledAsideMenu>
    );
};
